import type { Actionable } from './actions.js';
import type { AgentCardKind } from './index.js';
import type { OpCardRoute } from './routes.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * D · YORUM, LEAD, POLİTİKA VE TAKİP KARTLARI — alan ajanı D (Faz 8 dalga 2)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Dört tür: `comments`, `leads`, `policy_issue`, `tracking_health`. İlk ikisi
 * altyapının taslağında `creative.ts` (yorum) ve `ops.ts` (lead, politika)
 * içindeydi; KİŞİSEL VERİ ve GÜVENİLMEZ İÇERİK kuralları dördünde ortak
 * olduğu için tek alan dosyasına taşındı (30 Eyl 2026). Web görünümü
 * `tool-renderers/engagement.tsx`, API üreticileri `cards/engagement/`.
 *
 *   · 🔴 KİŞİSEL VERİ: yorum YAZARI ve METNİ, lead CEVAPLARI (ad, e-posta,
 *     telefon, form cevabı) karta YAZILMAZ — kart kimlik/sayı/durum taşır,
 *     görüntü verisi çizim anında canlı uçtan (`EngagementCardLiveData`).
 *     Sunucu kemeri (`finalize.ts`) kaçağı yakalarsa kart hiç çizilmez;
 *     `engagement.spec.ts` dört türün de kemerden TEMİZ geçtiğini kilitliyor.
 *   · 🔴 GÜVENİLMEZ İÇERİK: yorum metni ve lead cevabı üçüncü kişinin yazdığı
 *     serbest metin — ekranda DÜZ METİN (React metin düğümü), markdown/HTML yok.
 *   · 🔴 İNSAN KARARI: yanıt/gizle/sil/beğen, lead niteliği, itiraz/muafiyet
 *     düğmeleri kartın `actions`ında; sunucu kullanıcının rolüyle yürütür.
 *     Yıkıcı (yorum silme) kart içi onay adımı ister.
 *   · Metin DEĞİL anahtar: politika düzeltme önerisi, takip sorunu, eksik
 *     kurulum adımı `code` + `params` — ekran `cards.<tür>.*` ile çevirir.
 *     Platformun kendi ret nedeni (`reasons[].label`) platform metni:
 *     üçüncü kişi değil ama yine de düz metin çizilir.
 */

/** Yorumun geldiği yüzey — API `comments/comment-surfaces.ts` ile aynı adlar. */
export const COMMENT_CARD_SURFACES = ['facebook', 'instagram', 'tiktok', 'x', 'linkedin', 'youtube'] as const;
export type CommentCardSurface = (typeof COMMENT_CARD_SURFACES)[number];

/** Duygu — sunucunun sözlük tabanlı sezgisi (`comment-sentiment.ts`); `null` = belirsiz. */
export type CommentSentiment = 'positive' | 'neutral' | 'negative';

/**
 * Bir yüzeyin okunamama sebebi — ekran `cards.comments.unavailable.<code>`.
 * Müşteri metni: sunucu bayrağı / `.env` ADI taşımaz (geliştirici notu logda).
 */
export const COMMENT_UNAVAILABLE_CODES = [
  /** Özellik bu kurulumda henüz açılmadı (Mentorivo tarafı). */
  'feature_off',
  /** Bağlantıda gereken izin yok — yeniden bağlanmalı. */
  'scope_missing',
  /** Platform okumayı reddetti / geçici hata. */
  'read_failed',
  /** Liste platformda kırpıldı — yorumlar okunamadı. */
  'truncated',
  /** Bu platformda okunacak yayında reklam yok. */
  'no_ads',
  /** Okuma ücretli (X) — yalnız açıkça istenince okunuyor. */
  'on_request',
  /** Demo bağlantı — platforma gidilmiyor. */
  'demo',
] as const;
export type CommentUnavailableCode = (typeof COMMENT_UNAVAILABLE_CODES)[number];

export type EngagementAgentCard =
  /**
   * YORUMLAR — beş platformun normalleştirilmiş listesi (`list_ad_comments`).
   * Satır: kimlik + yüzey + zaman + duygu + durum. YAZAR ve METİN canlı uçtan;
   * yeni yorum SSE `comments` konusuyla (kart canlı veriyi tazeler, yeni satır
   * "yeni" rozetiyle — o satırın düğmesi yok: eylem kartın KAYDINDA doğar,
   * yeni satır için liste yeniden istenir).
   */
  | Actionable<{
      kind: 'comments';
      platforms: string[];
      scope: { level: 'account' | 'campaign' | 'ad'; ref: string | null; name: string | null };
      counts: { total: number; unanswered: number; negative: number };
      items: {
        /** Platformun yorum kimliği. */
        id: string;
        platform: string;
        surface: CommentCardSurface;
        /** Yerel reklam (UUID) — YouTube'da `null` (kanal geneli). */
        adId: string | null;
        /** Reklamın adı (markanın kendi adlandırması — kişi değil). */
        adName: string | null;
        parentId: string | null;
        createdAt: string | null;
        sentiment: CommentSentiment | null;
        hidden: boolean;
        /** Markanın yanıtı var (platform yanıt sayısı > 0 ya da marka yanıtı görüldü). */
        replied: boolean;
        /** Markanın KENDİ yorumu/yanıtı. */
        own: boolean;
      }[];
      /** Okunamayan yüzeyler — kart "neden yok" diye dürüst söyler. */
      unavailable: { platform: string; surface: CommentCardSurface | null; code: CommentUnavailableCode }[];
      /** Listede gösterilenden fazlası var (sayfa/birim sınırı). */
      truncated: boolean;
      /** Okuma anı (ISO) — "şu an itibarıyla". */
      readAt: string;
      /**
       * Canlı ucun YENİDEN okuyacağı işlemler — sunucunun yazdığı okuma
       * isteği (yüzey + reklam/ad set kimliği, sayfa boyu). Kimlik taşır,
       * kişisel veri taşımaz; istemci değiştiremez (kart sunucu kaydından okunur).
       */
      sources: { opId: string; surface: CommentCardSurface; adId: string | null; params: Record<string, unknown> }[];
    }>
  /**
   * LEAD'LER — maskeli liste (`list_leads`). Kart kimlik/zaman/durum taşır;
   * ad, e-posta, telefon, form cevapları canlı uçtan — VIEWER'a maskeli,
   * MEMBER ve üstüne açık. CSV dışa aktarma ayrı uç (süreli imzalı bağlantı,
   * ADMIN). Nitelik düğmesi (`qualify:<id>`) platforma dönüşüm geri bildirimi.
   */
  | Actionable<{
      kind: 'leads';
      platform: string;
      form: { ref: string | null; name: string | null } | null;
      range: { from: string; to: string } | null;
      total: number;
      /** Platformda daha fazla sayfa var. */
      truncated: boolean;
      items: {
        id: string;
        createdAt: string | null;
        formRef: string | null;
        adRef: string | null;
        campaignRef: string | null;
        status: string | null;
        /** Karttan niteliği bildirildi (`actionState`ten de okunur). */
        qualified: boolean | null;
        organic: boolean | null;
      }[];
      /**
       * Nitelik bildirimi mümkün mü: hedef (Google UPLOAD_CLICKS dönüşüm
       * eylemi / Meta CRM veri kümesi) biliniyorsa `ready`; değilse sebebi —
       * ekran `cards.leads.qualify.<state>`.
       */
      qualify: { state: 'ready' | 'needs_target' | 'unsupported'; target: string | null };
      /** CSV dışa aktarma açık mı (ADMIN — uç yine denetler). */
      exportable: boolean;
      /** Canlı uç ve CSV'nin yeniden okuyacağı işlem (form/reklam/tarih kimlikleri). */
      source: { opId: string; params: Record<string, unknown> } | null;
    }>
  /**
   * REDDEDİLEN / KISITLI REKLAMLAR — platformlar arası (`list_policy_issues`).
   * Küçük resim canlı uçtan (platform önizleme adresleri süreli).
   */
  | Actionable<{
      kind: 'policy_issue';
      items: {
        /** Kart içinde benzersiz: `<platform>:<ref>`. */
        id: string;
        platform: string;
        entity: {
          level: 'campaign' | 'adset' | 'ad' | 'creative' | 'keyword' | 'account';
          /** Platform kimliği. */
          ref: string | null;
          /** Yerel kimlik (UUID) — panel bağlantısı ve küçük resim. */
          localId: string | null;
          name: string | null;
        };
        /** `DISAPPROVED` | `LIMITED` | `IN_REVIEW` | platformun ham durumu. */
        status: string;
        reasons: {
          /** Platformun politika kodu/konusu (`TRADEMARKS_IN_AD_TEXT`, Meta ret kodu…). */
          code: string;
          /** Platformun kısa ret metni (varsa, ≤240) — düz metin. */
          label: string | null;
          /** Platformun resmî politika sayfası (https). */
          policyUrl: string | null;
        }[];
        /** `cards.policy_issue.fix.<code>` */
        fixes: { code: string; params?: Record<string, string | number> }[];
        appealable: boolean;
        exemptible: boolean;
      }[];
      total: number;
      /** Platformdan ayrıntı okunamayan yerel ret satırları (yalnız durum). */
      partial: boolean;
      checkedAt: string;
    }>
  /**
   * DÖNÜŞÜM TAKİBİ SAĞLIĞI — pixel / etiket / CAPI / dönüşüm eylemi
   * (`tracking_health`). Sorun ve eksik kurulum adımları anahtar.
   */
  | Actionable<{
      kind: 'tracking_health';
      items: {
        /** `<platform>:<type>:<ref>` */
        id: string;
        platform: string;
        type: 'pixel' | 'capi' | 'conversion_action' | 'conversion_tag' | 'event_set' | 'insight_tag';
        ref: string | null;
        name: string | null;
        status: 'healthy' | 'warning' | 'error' | 'unknown';
        lastEventAt: string | null;
        /** Son 24 saatte (platformun penceresi) alınan olay sayısı; bilinmiyorsa `null`. */
        events24h: number | null;
        /** `cards.tracking_health.issue.<code>` */
        issues: { code: string; params?: Record<string, string | number> }[];
      }[];
      /** Bağlı ama takip kaynağı OLMAYAN platformlar + kurulum adımları (`cards.tracking_health.step.<code>`). */
      missing: { platform: string; steps: { code: string; params?: Record<string, string | number> }[] }[];
      /** Okunamayan platformlar — `cards.tracking_health.unavailable.<code>`. */
      unavailable: { platform: string; code: string }[];
      checkedAt: string;
    }>;

export const ENGAGEMENT_CARD_KINDS = ['comments', 'leads', 'policy_issue', 'tracking_health'] as const;

/** Ekran araçları (`app-tools/engagement.tools.ts`) → kart. */
export const ENGAGEMENT_TOOL_CARDS: Readonly<Record<string, readonly AgentCardKind[]>> = {
  list_ad_comments: ['comments'],
  list_leads: ['leads'],
  list_policy_issues: ['policy_issue'],
  tracking_health: ['tracking_health'],
};

/**
 * Katalog okuma işlemi → kart. API üreticisi (`cards/engagement/index.ts`)
 * AYNI desenle (`cards.spec.ts`).
 */
export const ENGAGEMENT_OP_CARDS: readonly OpCardRoute<AgentCardKind>[] = [
  { match: '*.comment.list', kind: 'comments' },
  { match: 'meta.ig_comment.list', kind: 'comments' },
  { match: '*.lead.list', kind: 'leads' },
  { match: '*.ad.review_status', kind: 'policy_issue' },
  { match: 'google.policy.list_issues', kind: 'policy_issue' },
  { match: 'tiktok.review.get', kind: 'policy_issue' },
];

/** Canlı veri şekilleri — KAYDEDİLMEZ; kişisel/güvenilmez içerik yalnız burada. */
export interface EngagementCardLiveData {
  comments: {
    items: {
      id: string;
      platform: string;
      surface: CommentCardSurface;
      adId: string | null;
      /** Yorum yazarının görünen adı — kişisel veri, yalnız çizim için. */
      author: string | null;
      /** GÜVENİLMEZ düz metin. */
      text: string | null;
      likeCount: number | null;
      createdAt: string | null;
      hidden: boolean | null;
      own: boolean;
      sentiment: CommentSentiment | null;
      /** Kartın KAYDINDA olmayan (sonradan gelen) yorum — eylemi yok. */
      fresh: boolean;
    }[];
  };
  leads: {
    /** `full` — MEMBER ve üstü; `masked` — VIEWER (ad baş harfi, e-posta/telefon maskeli). */
    visibility: 'full' | 'masked';
    items: {
      id: string;
      /** Formun alan adı → değer (ad, e-posta, telefon, cevaplar). GÜVENİLMEZ düz metin. */
      answers: { field: string; value: string | null }[];
    }[];
  };
  policy_issue: {
    items: { id: string; thumbnailUrl: string | null; previewUrl: string | null }[];
  };
}
